import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { UserService } from 'src/app/services/user.service';

/**
 * Datos del titular para los eventos con entradas nominativas.
 *
 * Se abre al añadir al carrito, no en el checkout: el titular se congela en las
 * entradas cuando se genera el pedido (NominativeService::snapshotHolder, que
 * lee el perfil), así que con guardarlo aquí el resto del flujo de compra no
 * necesita saber nada de esto.
 *
 * Sale siempre en una compra nominativa, con los datos precargados, para que el
 * usuario confirme con qué nombre se va a emitir su entrada antes de que quede
 * fijado. Se guardan en su perfil (`users.name` y `users.nif`).
 *
 * Cierra devolviendo `true` si se ha guardado, y `undefined` si el usuario
 * cancela: quien lo abre solo debe continuar con la compra en el primer caso.
 */
@Component({
  selector: 'app-nominative-holder',
  templateUrl: './nominative-holder.component.html',
  styleUrls: ['./nominative-holder.component.scss']
})
export class NominativeHolderComponent implements OnInit {

  holderForm!: FormGroup;
  saving: boolean = false;
  errorMessage: string = '';

  private profile: any;

  constructor(
    private formBuilder: FormBuilder,
    private auth: AuthenticationService,
    private userService: UserService,
    private dialogRef: MatDialogRef<NominativeHolderComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) { }

  ngOnInit(): void {
    this.profile = this.auth.getProfile();

    // El documento no lleva validación de formato a propósito, igual que en el
    // alta de abonos y carnés: hay pasaportes y documentos extranjeros que no
    // encajan en el patrón del DNI/NIE. En el acceso se compara a ojo con el
    // documento físico, así que el formato no aportaría seguridad real.
    this.holderForm = this.formBuilder.group({
      name: [this.profile?.name || '', [Validators.required, Validators.maxLength(255)]],
      nif: [this.profile?.nif || '', [Validators.required, Validators.minLength(5), Validators.maxLength(255)]]
    });
  }

  // Tipados como FormControl (no AbstractControl|null) para que la plantilla
  // pueda leer invalid/dirty/touched sin comprobar null en cada expresión.
  get name(): FormControl { return this.holderForm.get('name') as FormControl; }
  get nif(): FormControl { return this.holderForm.get('nif') as FormControl; }

  submit() {
    if (this.holderForm.invalid || this.saving) {
      this.holderForm.markAllAsTouched();
      return;
    }

    const name = (this.holderForm.value.name || '').trim();
    // Se normaliza igual que en el servidor (User::normalizeDocument), para que
    // el perfil que queda en memoria coincida con lo que se guarda y no se
    // reenvíe el mismo documento en la siguiente compra.
    const nif = (this.holderForm.value.nif || '').trim().toUpperCase().replace(/[\s-]/g, '');

    // Si no ha cambiado nada, no se gasta una llamada en reescribir lo mismo.
    if (name === (this.profile?.name || '') && nif === (this.profile?.nif || '')) {
      this.dialogRef.close(true);
      return;
    }

    this.saving = true;
    this.errorMessage = '';

    this.userService.putUser(this.profile.id, { name, nif }).subscribe({
      next: () => {
        // Refrescar el perfil en memoria: el resto del flujo de compra lo lee de
        // aquí, y el backend rechaza el pedido si no ve nombre y documento.
        this.auth.setProfile({ ...this.profile, name, nif });
        this.saving = false;
        this.dialogRef.close(true);
      },
      error: () => {
        this.saving = false;
        this.errorMessage = 'No hemos podido guardar tus datos. Inténtalo de nuevo.';
      }
    });
  }

  cancel() {
    this.dialogRef.close();
  }
}
